import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Popup, Header, Icon, Divider } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { Score, Table } from 'components';
import { decideCurrency, isNB } from 'utils/helpers';

const TABLE_STYLE = {
  tableLayout: 'fixed',
  width: '100%',
};

const TABLE_COLUMN_WIDTHS = [
  '18.75%',
  '20.3125%',
  '20.3125%',
  '20.3125%',
  '20.3125%',
];

@connect((state) => ({
  user_region: state.Storage.user_region || 'CA',
  french: state.Storage.language === 'fr',
  partnerName: state.Storage['partner-name'],
}))
export default class Downside extends Component {
  getPercent = (num) => (num ? `${(num * 100).toFixed(2)} %` : '-');
  getDownside = (num) => (num ? `${(num * 100).toFixed(2)} %` : '0%');
  getVaR = (num) => (this.hasValue(num) ? `${(num * 100).toFixed(2)} %` : '-');
  getVaRAbs = (num) =>
    this.hasValue(num) ? (Math.abs(num) * 100).toFixed(2) : '-';
  hasValue = (num) => num !== null && num !== undefined && !Number.isNaN(num);

  parseDate = (portfolio) => {
    const { french } = this.props;
    const init = portfolio.init_date.split('-');
    const end = portfolio.end_date.split('-');
    const months = french
      ? {
          '01': 'Janvier',
          '02': 'Février',
          '03': 'Mars',
          '04': 'Avril',
          '05': 'Mai',
          '06': 'Juin',
          '07': 'Juillet',
          '08': 'Août',
          '09': 'Septembre',
          10: 'Octobre',
          11: 'Novembre',
          12: 'Décembre',
        }
      : {
          '01': 'January',
          '02': 'February',
          '03': 'March',
          '04': 'April',
          '05': 'May',
          '06': 'June',
          '07': 'July',
          '08': 'August',
          '09': 'September',
          10: 'October',
          11: 'November',
          12: 'December',
        };

    return `${french ? 'de ' : ''}${months[init[1]]} ${init[0]} ${
      french ? 'à' : 'to'
    } ${months[end[1]]} ${end[0]}`;
  };

  renderVaR = () => {
    const { data, french } = this.props;
    const VaR =
      data.fratios && data.fratios.portfolio && data.fratios.portfolio.VaR;

    // Only display when at least one VaR value is returned
    if (!VaR || !['p1', 'p5', 'p10'].some((key) => this.hasValue(VaR[key]))) {
      return (
        <p>
          <Header style={{ marginTop: '2rem' }}>
            <Header.Content>
              {french ? 'Valeur à risque' : 'Value-at-Risk'}
            </Header.Content>
            <Header.Subheader>
              {french
                ? 'La mesure de la valeur à risque (VaR) s’affichera lorsqu’un portefeuille aura au moins 10 ans d’historique.'
                : 'The metric, Value-at-Risk, will show when a portfolio has 10 or more years of history.'}
            </Header.Subheader>
          </Header>
        </p>
      );
    }

    const columns = [
      {
        key: 'p1',
        label: french ? 'VaR à 1 %' : '1% VaR',
        pct: 1,
        freq: 100,
      },
      {
        key: 'p5',
        label: french ? 'VaR à 5 %' : '5% VaR',
        pct: 5,
        freq: 20,
      },
      {
        key: 'p10',
        label: french ? 'VaR à 10 %' : '10% VaR',
        pct: 10,
        freq: 10,
      },
    ];

    const p5Example = this.hasValue(VaR.p5) ? this.getVaRAbs(VaR.p5) : '5';

    return (
      <React.Fragment>
        <Header style={{ marginTop: '2rem' }}>
          <Header.Content>
            {french ? 'Valeur à risque' : 'Value-at-Risk'}&nbsp;
            <Popup
              trigger={
                <Icon
                  name="question circle outline"
                  style={{
                    fontSize: '15px',
                    verticalAlign: 'initial',
                    color: 'dimgrey',
                  }}
                />
              }
              position="top center"
              content={
                french
                  ? `La valeur à risque (VaR) indique la perte maximale que le portefeuille pourrait subir au cours d’un mauvais mois. Par exemple, une VaR à 5 % de -${p5Example} % signifie qu’il y a une probabilité de 5 % de perdre plus de ${p5Example} % au cours d’un mois donné. À titre informatif seulement; non incluse dans la note de risque de pertes.`
                  : `Value-at-Risk (VaR) shows how much the portfolio could lose in a bad month. For example, a 5% VaR of -${p5Example}% means there is a 5% chance of losing more than ${p5Example}% in a given month. For information only; not included in the Downside score.`
              }
              wide
            />
          </Header.Content>
          <Header.Subheader>
            {french
              ? 'Perte minimale estimée sur un mois à chaque niveau de probabilité'
              : 'Estimated minimum one-month loss at each probability level'}
          </Header.Subheader>
          <Header.Subheader>
            {french
              ? 'Selon les rendements mensuels des 10 dernières années'
              : 'Based on the past 10 years of monthly returns'}
          </Header.Subheader>
        </Header>

        <Divider />

        <Table basic textAlign="center" style={TABLE_STYLE}>
          <colgroup>
            {TABLE_COLUMN_WIDTHS.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              {columns.map((col) => (
                <Table.HeaderCell key={col.key}>
                  {col.label}&nbsp;
                  <Popup
                    trigger={
                      <Icon
                        name="question circle outline"
                        style={{
                          fontSize: '15px',
                          verticalAlign: 'initial',
                          color: 'dimgrey',
                        }}
                      />
                    }
                    position="top center"
                    content={
                      french
                        ? `Il y a une probabilité de ${
                            col.pct
                          } % (environ 1 mois sur ${
                            col.freq
                          }) de perdre plus de ${this.getVaRAbs(
                            VaR[col.key]
                          )} % au cours d’un mois donné.`
                        : `There is a ${col.pct}% chance (about 1 month in ${
                            col.freq
                          }) of losing more than ${this.getVaRAbs(
                            VaR[col.key]
                          )}% in a given month.`
                    }
                    wide
                  />
                </Table.HeaderCell>
              ))}
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row positive>
              <Table.Cell
                width={3}
                style={{
                  textAlign: 'center',
                  paddingLeft: 0,
                }}
              >
                {french ? 'Mon portefeuille' : 'My Portfolio'}
              </Table.Cell>
              {columns.map((col) => (
                <Table.Cell key={col.key}>
                  {this.getVaR(VaR[col.key])}
                </Table.Cell>
              ))}
              <Table.Cell />
            </Table.Row>
          </Table.Body>
        </Table>
      </React.Fragment>
    );
  };

  render() {
    const { benchmark, data, user_region, french, partnerName } = this.props;

    return (
      <Container>
        <Score
          name={french ? 'Risque de pertes ' : 'Downside '}
          score={`${data.score.Volatility[benchmark][0]}%`}
          grade={data.score.Volatility[benchmark][1]}
          description={
            french
              ? 'Évalué selon les trois mesures du risque à la baisse, par rapport à l’indice de référence. Si votre portefeuille se contente d’égaler (plutôt que de surpasser) l’indice de référence, il obtiendra une note de 65 %.'
              : 'Scored on the three downside risk measures, relative to the benchmark. If your portfolio is just matching (rather than outperforming) the benchmark, it will receive a score of 65%.'
          }
          portfolio={data.portfolio}
          extraInfo={
            french
              ? 'Par rapport à votre indice de référence'
              : 'Relative to your benchmark'
          }
        />

        <p>
          {french
            ? 'La protection contre les baisses découle de la capacité de votre portefeuille à limiter les pertes en cas de baisse du cours du marché.'
            : "Downside protection speaks to your portfolio's ability to limit losses when there is a market downturn."}
        </p>
        <p>
          {french
            ? 'En gestion de portefeuille, la mesure traditionnelle du risque (ou de la volatilité) est l’écart-type des rendements historiques d’un portefeuille. Il s’agit d’une mesure du risque '
            : "In portfolio management, the traditional measure of risk (or volatility) is the standard deviation of a portfolio’s historical returns. It is a measure of the portfolio's "}
          <strong>total</strong>{' '}
          {french
            ? 'du portefeuille. Il est présenté sous forme de pourcentage annualisé pour faciliter la comparaison, peu importe le portefeuille ou la période.'
            : 'risk. It is reported as an annualized percentage for easy comparison across portfolios and time periods.'}
        </p>
        <p>
          {french
            ? 'Il est important de noter que le risque total comprend la possibilité de gains et de'
            : 'It is important to note that total risk has an upside and a'}{' '}
          <strong>{french ? 'pertes' : 'downside'}</strong>
          {french
            ? '. Au fil du temps, les rendements du portefeuille varieront au-dessus de la moyenne (ou de votre objectif de rendement), mais aussi '
            : '. Over time, portfolio returns will fluctuate above the average (or your target return), as well as '}
          <strong>{french ? 'en dessous' : 'below'}</strong>
          {french
            ? '. Par instinct, et aussi car c’est généralement un comportement rationnel, nous avons tendance à nous préoccuper davantage du risque de pertes que du risque de gains.'
            : '. It is human nature and often rational to be more concerned about downside risk than upside risk.'}
        </p>

        <Header>
          <Header.Content>
            {french ? 'Statistiques clés' : 'Key Statistics'}
          </Header.Content>
          <Header.Subheader>
            {french
              ? 'Selon la composition actuelle de votre portefeuille'
              : 'Based on the current composition of your portfolio'}
            &nbsp;
            <Popup
              trigger={
                <Icon
                  name="question circle outline"
                  style={{
                    fontSize: '15px',
                    verticalAlign: 'initial',
                    color: 'dimgrey',
                  }}
                />
              }
              position="top center"
              content={
                french
                  ? 'En utilisant les mêmes avoirs et les mêmes pondérations.'
                  : 'Using the same holdings and the same weights.'
              }
              wide
            />
          </Header.Subheader>
          <Header.Subheader>
            {`${this.parseDate(data.portfolio)} ${
              french ? '(inclusivement) en' : '(inclusive) in'
            } ${decideCurrency(data, user_region || 'CA')}`}
          </Header.Subheader>
        </Header>
        <Divider />

        <Table basic textAlign="center" style={TABLE_STYLE}>
          <colgroup>
            {TABLE_COLUMN_WIDTHS.map((width, index) => (
              <col key={index} style={{ width }} />
            ))}
          </colgroup>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>
                {french ? 'Risque total' : 'Total Risk'}&nbsp;
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  position="top center"
                  content={
                    french
                      ? 'Il s’agit d’une mesure des fluctuations des rendements de votre portefeuille dans le temps, mesurée par l’écart-type. Il comprend les fluctuations haussières et baissières, d’où le nom de risque total.'
                      : "This is the variability of your portfolio's returns over time, as measured by the standard deviation. It includes upside as well as downside variability, and hence the name total risk."
                  }
                  wide
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                {french ? 'Risque de perte' : 'Downside Risk'}&nbsp;
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  position="top center"
                  content={
                    french
                      ? 'Il s’agit d’une mesure des fluctuations des rendements de votre portefeuille en dessous d’un objectif. L’objectif visé ici est la préservation du capital, c’est-à-dire un rendement qui n’est pas négatif.'
                      : "This is the variability of your portfolio's returns below a target. The target used here is capital preservation; that is, a non-negative return."
                  }
                  wide
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                {french ? 'Baisse maximale' : 'Maximum Drawdown'}&nbsp;
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  position="top center"
                  content={
                    french
                      ? 'Il s’agit de la perte maximale de crête à creux observée pendant la période analysée. Elle donne une perspective différente sur le risque de pertes d’un portefeuille.'
                      : "This is the maximum peak to trough loss observed during the period in question. It gives a different perspective of a portfolio's downside risk."
                  }
                  wide
                />
              </Table.HeaderCell>
              <Table.HeaderCell>
                {french ? 'Capture à la baisse' : 'Downside Capture'}&nbsp;
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  position="left center"
                  content={
                    french
                      ? 'Quantifie la perte relative de votre portefeuille par rapport au marché boursier (tel qu’il a été indiqué) durant une baisse du marché. Une capture à la baisse inférieure à 100 % indique que votre portefeuille a perdu moins que le marché au cours de ces périodes. Une valeur de capture à la baisse négative indique que votre portefeuille a plutôt connu un rendement positif lorsque le marché boursier était dans le rouge.'
                      : 'This measures whether your portfolio lost less than the stock market (as selected) when there was a market downturn. A downside capture of less than 100% indicates that your portfolio lost less than the stock market during those periods. A downside capture that is negative indicates that your portfolio actually generated positive returns when the stock market was in the red.'
                  }
                  wide
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row positive>
              <Table.Cell>
                {french ? 'Mon portefeuille' : 'My Portfolio'}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios.portfolio.stdDev)}
              </Table.Cell>
              <Table.Cell>
                {this.getDownside(data.fratios.portfolio.dsr)}
              </Table.Cell>
              <Table.Cell>
                {this.getDownside(data.fratios.portfolio.maxDd)}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios.portfolio.dscapture[benchmark])}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell width={3}>
                {data.fratios[benchmark].name}
                <Popup
                  trigger={
                    <Icon
                      name="question circle outline"
                      style={{
                        fontSize: '15px',
                        verticalAlign: 'initial',
                        color: 'dimgrey',
                      }}
                    />
                  }
                  position="top center"
                  content={data.fratios[benchmark].description}
                  wide
                />
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios[benchmark].stdDev)}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios[benchmark].dsr)}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios[benchmark].maxDd)}
              </Table.Cell>
              <Table.Cell>
                {this.getPercent(data.fratios[benchmark].dscapture)}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
        {!isNB(partnerName) && this.renderVaR()}
      </Container>
    );
  }
}

Downside.propTypes = {
  data: PropTypes.object.isRequired,
  benchmark: PropTypes.string.isRequired,
  user_region: PropTypes.string,
  partnerName: PropTypes.string,
};

Downside.defaultProps = {
  user_region: 'CA',
  partnerName: '',
};

const Container = styled.div`
  min-height: 100%;
  padding: 2rem 2rem 3rem 2rem;
`;
